import React, { useEffect, useRef, useState, ReactNode } from "react";
import { observer } from "mobx-react-lite";
import pageBuilderStore from "../../../stores/pageBuilderStore"; 
import type { DragSourceMonitor } from "react-dnd";
import { useDrag, useDrop } from "react-dnd";
import { IDesignBlock } from "../../../types/iblock";
import clsx from "clsx";
import { GripVertical } from "lucide-react";
import { getEmptyImage } from "react-dnd-html5-backend";
import { IContentBlock } from "../../../types/iblock";

/**
 * Props for the {@link DesignBlock} wrapper component.
 *
 * @interface IDesignBlockProps
 * @extends IDesignBlock
 * 
 * @description
 * Defines the props required to render and interact with a single draggable
 * and editable block within the PageBuilder's canvas. Used in both `DesignCanvas`
 * and `ColumnCell` contexts. Supports reordering, deletion, drag state, and live editing state.
 *
 * @since 2025-06-14
 */
interface IDesignBlockProps extends IDesignBlock {
  /** Optional handler to trigger deletion of this block */
  onDelete?: () => void;

  /** The content component (e.g. HeadingBlock, TextBlock) rendered inside this wrapper */
  children: ReactNode;

  /** The content block that this DesignBlock will contain. */
  contentBlock: IContentBlock;

  /**
   * Index of the column this block belongs to (only set when used inside a ColumnCell).
   * Used for determining cross-column reordering.
   */
  colIndex?: number;

  /** If true, block is currently being dragged (affects styling) */
  isDragging?: boolean;

  /** If true, we render the background as white. */
  isDragPreview?: boolean;

  /** If true, block is actively being edited (used to show focus, border highlights) */
  isEditing?: boolean;

  /** If true, block is currently hovered by mouse (affects handle visibility) */
  isHovered?: boolean;

  /** A new payload value is needed when dragging a DesignBlock from ColumnCell to DesignCanvas */
  colContentBlockId?: string;

  /** Context from where this block originates (e.g. "canvas" | "columns") */
  blockContext: string;

  /** Triggered when the mouse enters the block (used for hover effects or ref tracking) */
  onMouseEnter?: () => void;

  /** Triggered when the mouse leaves the block */
  onMouseLeave?: () => void;

  /** Called when the drag operation begins — passes the block's ID */
  onDragStart?: (blockId: string) => void;

  /** Called when the drag operation ends */
  onDragEnd?: () => void;

  /**
   * Optional handler fired when another block is dragged over this block.
   * Used by `DesignCanvas` or `ColumnCell` to track potential drop targets.
   *
   * @param hoveredOverId - ID of the block currently being hovered over
   */
  onHover?: (hoveredOverId: string) => void;
}

/**
 * DesignBlock – Wrapper component for all drag-and-drop content blocks in the builder.
 *
 * @component
 * @description
 * DesignBlock is a reusable layout wrapper used to visually and structurally wrap individual content blocks
 * (e.g., HeadingBlock, TextBlock) inside the DesignCanvas. It enables block-level interactions such as deletion,
 * border highlighting, and positioning for drag-and-drop operations. Each DesignBlock manages its own
 * DOM ref, visual state transitions, and optionally supports editing and drag styling.
 *
 * @features
 * - Visual border indicators for normal, hover, editing, and dragging states
 * - Optional delete button shown on hover (for block-level deletion)
 * - Supports consistent padding and layout styling across all content types
 * - Ref forwarding to allow GlideBar and scroll-aware positioning utilities
 * - Accepts `children` as the rendered content block component
 * - Integrates with `react-dnd` to support draggable behavior via left handlebar
 * - Distinguishes between editing interaction and drag intent via handle click
 * - Deleting a block from the `DesignCanvas` is now working as well.
 *
 * @props
 * @param {string} designBlockId - Unique ID used for rendering and diffing (matches designBlockId)
 * @param {RefObject<HTMLDivElement>} designBlockRef - DOM ref for anchoring toolbars or observers
 * @param {() => void} [onDelete] - Optional delete handler triggered by 🗑️ button
 * @param {boolean} [isDragging] - Optional flag to indicate active drag styling
 * @param {boolean} [isEditing] - Optional flag to indicate focused edit styling
 * @param {(blockId: string) => void} [onDragStart] - Optional callback fired when drag begins
 * @param {() => void} [onDragEnd] - Optional callback fired when drag ends
 * @param {ReactNode} children - The inner content block to render (TextBlock, HeadingBlock, etc.)
 *
 * @usage
 * <DesignBlock
 *   designBlockId="abc123"
 *   designBlockRef={ref}
 *   onDelete={() => removeBlock("abc123")}
 *   isEditing={true}
 *   onDragStart={(id) => console.log("drag started", id)}
 * >
 *   <TextBlock ... />
 * </DesignBlock>
 *
 * @since 2025-06-15
 * @lastUpdated 2025-06-21
 *
 * @devLogs
 * - Integrated react-dnd dragRef + dropRef to enable block-level dragging
 * - Refactored DOM structure to apply dragRef to container and blockRef to block-only
 * - Synced handlebar visual states with edit vs drag intent
 * - Introduced color transitions and border radius separation for more intuitive UX
 *
 * @observations
 * - Using `group-hover` + `transition` enables smooth handlebar entrance UX
 * - `dragDropRef` ensures the DesignBlock remains both draggable and a drop target (if needed in future)
 * - Handlebar is purposefully separated visually and structurally for better click targeting
 */
export default observer(function DesignBlock({ 
  designBlockId,
  designBlockRef,
  blockContext,
  colContentBlockId,
  colIndex,
  contentBlock,
  onDelete, 
  children, 
  isDragging, 
  isDragPreview = false,
  isEditing,
  isHovered,
  onDragStart,
  onDragEnd,
  onMouseEnter,
  onMouseLeave,
}: IDesignBlockProps) {

  // Indicates if the handle is actively clicked (not just hovered)
  const [isHandleActive, setIsHandleActive] = useState(false);
  const handleBarRef = useRef<HTMLDivElement>(null);
  
  // Base layout and border rounding logic based on editing state
  const baseClasses = `relative group p-2 ${isDragPreview && "bg-white"} transition-all ${isEditing ? "rounded-none" : "rounded-r-md"}`;

  // Controls border visibility and color for drag/edit/hover modes
  const borderClasses = isDragging
    ? "border-4 border-l-[1px] border-violet-700 opacity-100" : isHandleActive
      ? "border-2 border-blue-500 opacity-100" : isEditing
        ? "border-2 border-blue-500 opacity-80"
        : "border-2 border-transparent";

  /**
   * Enabled the current DesignBlock to be draggable via react-dnd.
   * 
   * The `item` payload contains metadata used by destination drop targets.
   * - `item.type`: We need this so that the DesignBlock is recognized in the validation process.
   * - `designBlockId`: Unique identifier of this DesignBlock (or when it is being dragged).
   * - `context`: Does this DesignBlock come from ( "canvas"  |  "columns")?
   * - `fromColIndex`: The column index use for cross-column tracking.
   * - `contentBlockRef`: Needed when doing canvas-wide reordering (i.e. canvas-to-cell, vice versa)
   * - `contentBlock`: The main payload of the DesignBlock (Content Block that we want to render.)
   */
  const [{ isDragging: isBlockDragging }, dragRef, previewRef] = useDrag<
    {
      blockType: string,
      designBlockId: string, 
      context: string, 
      otherColContentBlockId?: string,
      fromColIndex?: number, 
      widthScale: number,
      contentBlock: IContentBlock },
    unknown,
    { isDragging: boolean }
  >({
    type: "DESIGN_BLOCKS",
    item: {
      blockType: "DESIGN_BLOCKS",
      designBlockId: designBlockId,
      context: blockContext,
      otherColContentBlockId: colContentBlockId ?? "",
      fromColIndex: colIndex ?? -1, // fallback -1 if undefined (from canvas)
      widthScale: blockContext === "canvas" ? 1 : 0.4,
      contentBlock: contentBlock
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => { 
    previewRef(getEmptyImage(), { captureDraggingState: true });
  }, []);

  // Effect to notify parent when drag starts or ends
  useEffect(() => {
    if (isBlockDragging) {
      onDragStart?.(designBlockId);
    } else {
      onDragEnd?.();
    }
  }, [isBlockDragging]);

  useEffect(() => {
    if (!isBlockDragging) {
      setIsHandleActive(false); // 🧼 Reset handle active state
    }
  }, [isBlockDragging]);

  // Combined ref for drag+drop behaviors
  dragRef(handleBarRef);
  
  return (
    <div 
      className={`relative group transition-all duration-200 pl-[2px] ${!isDragPreview && "ml-4"}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >

      {/* Handlebar */}
      <div
        ref={handleBarRef}
        className={clsx(
          "absolute left-[-14px] top-0 h-full w-[18px] rounded-l-md flex items-center justify-center",
          "bg-blue-600 transition-all duration-300 ease-out transform",
          isDragging
            ? "opacity-100 bg-violet-800 translate-x-0"
            : isHandleActive
            ? "opacity-100 translate-x-0"
            : isEditing
            ? "opacity-60 translate-x-0"
            : isHovered ? "group-hover:opacity-100 group-hover:translate-x-0" : "opacity-0 translate-x-full"
        )}
        onMouseDown={() => setIsHandleActive(true)}
        onMouseUp={() => setIsHandleActive(false)}
      >
        <GripVertical size={16} className="text-white opacity-90" />
      </div>

      {/* DesignBlock is here */}
      <div
        ref={designBlockRef}
        className={clsx(
          baseClasses,
          "rounded-r-md transition-all duration-200", // only right corners
          borderClasses,
          isHovered && "group-hover:border-blue-500 group-hover:opacity-80"
        )}
      >
        {/* Render delete button in top-right on hover */}
        {onDelete && (
          <button
            className={`absolute top-0 right-0 z-10 bg-white/70 text-red-500 p-1 rounded shadow-sm opacity-0 ${isHovered && "hover:bg-white group-hover:opacity-100"} transition-opacity`}
            onClick={onDelete}
          >
            🗑️
          </button>
        )}

        {/* Inject actual content block */}
        <div className={`${isDragPreview && "bg-white"}`}>{children}</div>
      </div>
    </div>
  );
});